import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

interface PasteImageUploadOptions {
  uploadFn: (file: File) => Promise<string>;
  onError?: (error: Error) => void;
}

export const PasteImageUpload = Extension.create<PasteImageUploadOptions>({
  name: 'pasteImageUpload',

  addOptions() {
    return {
      uploadFn: async () => {
        throw new Error('Upload function not provided');
      },
      onError: (error: Error) => {
        console.error('Image upload error:', error);
      },
    };
  },

  addProseMirrorPlugins() {
    const { uploadFn, onError } = this.options;
    const editor = this.editor;

    return [
      new Plugin({
        key: new PluginKey('pasteImageUpload'),
        props: {
          handlePaste(view, event) {
            const items = Array.from(event.clipboardData?.items || []);
            const imageItems = items.filter(item => item.type.indexOf('image') !== -1);

            if (imageItems.length === 0) {
              return false;
            }

            event.preventDefault();

            imageItems.forEach(item => {
              const file = item.getAsFile();
              if (!file) return;

              // Insert loading placeholder
              const { schema } = view.state;
              const loadingNode = schema.text('⏳ Uploading image...');
              const transaction = view.state.tr.replaceSelectionWith(loadingNode);
              const loadingPos = transaction.selection.from - loadingNode.nodeSize;
              view.dispatch(transaction);

              // Upload image
              uploadFn(file)
                .then(url => {
                  // Replace loading placeholder with actual image
                  const { tr } = view.state;
                  const currentNode = view.state.doc.nodeAt(loadingPos);
                  
                  if (currentNode && currentNode.text?.includes('Uploading image')) {
                    const imageNode = schema.nodes.image.create({ src: url });
                    const deleteTr = tr.replaceWith(
                      loadingPos,
                      loadingPos + currentNode.nodeSize,
                      imageNode
                    );
                    view.dispatch(deleteTr);
                  } else {
                    // If placeholder was moved/deleted, insert at current position
                    editor.commands.setImage({ src: url });
                  }
                })
                .catch(error => {
                  // Remove loading placeholder on error
                  const { tr } = view.state;
                  const currentNode = view.state.doc.nodeAt(loadingPos);
                  
                  if (currentNode && currentNode.text?.includes('Uploading image')) {
                    const deleteTr = tr.delete(loadingPos, loadingPos + currentNode.nodeSize);
                    view.dispatch(deleteTr);
                  }
                  
                  if (onError) {
                    onError(error);
                  }
                });
            });

            return true;
          },
        },
      }),
    ];
  },
});
